import { connectUtil, PropsFromRedux } from 'base/src/utils/reduxUtil';
import { useEffect, useMemo, useState } from 'react';
import { formatDate } from 'base/src/utils/stringUtil';
import { formatToBRL } from 'base/src/utils/mathUtil';
import Avatar from 'base/src/components/Avatar';
import { Tag } from 'base/src/components/Tag';
import { useNavigate } from 'react-router';
import { GetPagamentos } from '../../../../store/pagamento/actions/pagamentoAction';
import { RootStateCarteira } from '../../../../store/carteiraReducer';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  AvatarGroup,
  Box,
} from '@mui/material';
import NoData from 'base/src/components/NoData/index';
import { HoverCard, HoverCardArrow, HoverCardContent, HoverCardPortal, HoverCardTrigger } from 'base/src/components/HoverCard';
import HoverAvatar from '../../../../components/HoverAvatar';
import { Tooltip, TooltipContent, TooltipTrigger } from 'base/src/components/Tooltip';
import { useTheme } from 'base/src/utils/context/themeContext';

const connector = connectUtil(
  (state: RootStateCarteira) => ({
    Pagamentos: state.PagamentoReducer.Pagamento,
    CarteiraUsuario: state.CarteiraBaseReducer.CarteiraUsuario,
  }),
  { GetPagamentos }
);

export interface HistoricoPagamentoComponentProps extends PropsFromRedux<typeof connector> {}

function PagamentosCancelados({ GetPagamentos, Pagamentos, CarteiraUsuario }: HistoricoPagamentoComponentProps) {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (CarteiraUsuario.length > 0) {
      GetPagamentos({ From: '', To: '' }, CarteiraUsuario[0].CarteiraID, () => { });
    }
  }, [GetPagamentos, CarteiraUsuario]);

  const groupedPagamentos = useMemo(() => {
    return Pagamentos.filter(pagamento =>
      pagamento.Pagos.some(pago => pago.Status === 'CANCELLED' || pago.Status === 'ERROR' || pago.Status === 'REFUNDERROR')
    ).reduce((acc, pagamento) => {
      const key = pagamento.DataOperacao;
      acc[key] = acc[key] || [];
      acc[key].push(pagamento);
      return acc;
    }, {} as Record<string, typeof Pagamentos>);
  }, [Pagamentos]);

  const tableData = useMemo(() => {
    return Object.entries(groupedPagamentos).map(([dataOperacao, pagamentos]) => {
      const uniqueAvatars = Array.from(
        new Map(
          pagamentos.flatMap(p => p.Pagos).map(pago => [pago.Avatar, pago])
        ).values()
      );

      const totalItems = Math.max(1, pagamentos.reduce((sum, p) => sum + p.QuantidadePremiacoesPagas, 0));
      const totalValue = pagamentos.reduce((sum, pagamento) => sum + Number(pagamento.ValorTransacao), 0);

      return {
        dataOperacao,
        totalItems,
        totalValue,
        uniqueAvatars,
        totalRecipients: pagamentos.flatMap(p => p.Pagos).length,
      };
    });
  }, [groupedPagamentos]);

  const paginatedData = useMemo(() => {
    return tableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [tableData, page, rowsPerPage]);

  function handleChangePage(_event: unknown, newPage: number) {
    setPage(newPage);
  }

  function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }

  function handleRowClick(dataOperacao: string) {
    return () => navigate(`/pagamento/${dataOperacao}`);
  }

  if (tableData.length === 0) {
    return <NoData />;
  }

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          backgroundColor: theme.colors.card,
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.colors.background }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Data</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Itens</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem', minWidth: 250 }}>Destinatários</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Chave Pix</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Valor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow
                key={row.dataOperacao}
                onClick={handleRowClick(row.dataOperacao)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: theme.colors.background,
                  },
                  transition: 'background-color 0.2s',
                }}
              >
                <TableCell sx={{ fontSize: '0.95rem' }}>{formatDate(row.dataOperacao)}</TableCell>
                <TableCell sx={{ fontSize: '0.95rem' }}>{row.totalItems} item(s)</TableCell>
                <TableCell>
                  <Tooltip>
                    <TooltipTrigger style={{ background: 'transparent', border: 'none', padding: 0 }}>
                      <Tag variant="danger">Cancelado</Tag>
                    </TooltipTrigger>
                    <TooltipContent>
                      Algum(s) dos pagamentos foram cancelados por motivos diversos, clique para detalhar.
                    </TooltipContent>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {row.uniqueAvatars.slice(0, 2).map((pago, idx) => (
                      <HoverCard key={idx}>
                        <HoverCardTrigger asChild>
                          <Box
                            component="button"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              background: 'transparent',
                              border: 'none',
                              padding: 0,
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            <Avatar
                              size="sm"
                              alt={pago.UsuarioNome || 'User avatar'}
                              fallBack={pago.UsuarioNome?.substring(0, 2) || 'U'}
                              borderColor="transparent"
                              src={pago.Avatar}
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                              <Box
                                component="span"
                                sx={{
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  color: theme.colors.text,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {pago.UsuarioNome}
                              </Box>
                              <Box
                                component="span"
                                sx={{
                                  fontSize: '0.75rem',
                                  color: theme.colors.textSecondary || theme.colors.text,
                                  opacity: 0.7,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {pago.Email}
                              </Box>
                            </Box>
                          </Box>
                        </HoverCardTrigger>
                        <HoverCardPortal>
                          <HoverCardContent side="top">
                            <HoverCardArrow />
                            <HoverAvatar
                              user={{
                                NomeCompleto: pago.UsuarioNome,
                                UsuarioAcesso: pago.UsuarioAcesso,
                                CPF: pago.UsuarioCpf,
                                Email: pago.Email,
                                ChavePix: pago.ChavePix,
                                ImagemUrl: pago.Avatar,
                                TelefoneCelular: '',
                                TelefoneFixo: '',
                                UsuarioID: 0,
                                DataNascimento: new Date(),
                                NomeExibicao: pago.UsuarioNome,
                                Token: '',
                                TokenPushNotification: '',
                                ManterConectado: false,
                                EmpresaID: 0,
                              }}
                            />
                          </HoverCardContent>
                        </HoverCardPortal>
                      </HoverCard>
                    ))}
                    {row.totalRecipients > 2 && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: '0.8rem',
                          color: theme.colors.primary,
                          fontWeight: 500,
                          pl: 0.5,
                        }}
                      >
                        +{row.totalRecipients - 2} destinatário(s)
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontSize: '0.875rem' }}>
                  {row.uniqueAvatars.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {row.uniqueAvatars.slice(0, 2).map((pago, idx) => (
                        <Box key={idx} component="span" sx={{ fontSize: '0.875rem' }}>
                          {pago.ChavePix}
                        </Box>
                      ))}
                      {row.totalRecipients > 2 && (
                        <Box component="span" sx={{ fontSize: '0.75rem', opacity: 0.7 }}>
                          +{row.totalRecipients - 2} mais
                        </Box>
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {formatToBRL(row.totalValue)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={tableData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </TableContainer>
    </Box>
  );
}

export default connector(PagamentosCancelados);
